import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type Branch = {
  id: string;
  tenant_id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type BranchWithStats = Branch & {
  total_students: number;
  total_instructors: number;
  total_vehicles: number;
  total_users: number;
};

export type CreateBranchInput = {
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  isActive?: boolean;
};

export type UpdateBranchInput = {
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
};

export function useBranchList(filter: { isActive?: boolean } = {}): {
  data: BranchWithStats[] | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<BranchWithStats[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const isActive = filter.isActive;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (isActive !== undefined) params.is_active = String(isActive);
    api
      .get<BranchWithStats[]>("/branches", params, { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Şube listesi Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [isActive, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

export async function createBranch(input: CreateBranchInput): Promise<Branch> {
  return api.post<Branch>("/branches", input);
}

export async function updateBranch(id: string, input: UpdateBranchInput): Promise<Branch> {
  return api.put<Branch>(`/branches/${id}`, input);
}

export async function setBranchActive(id: string, isActive: boolean): Promise<Branch> {
  return api.post<Branch>(`/branches/${id}/active`, { isActive });
}
